import * as React from "react";
import { useState, useMemo, useEffect } from "react";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { AntDTable } from "@/common/components/ui";
import { Card, CardContent } from "@/common/components/ui/card";
import { Search, RefreshCw, Download, Mail } from "lucide-react";
import { TableColumnsType } from "antd";
import { useQuery } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import PublicLeadsService from "@/services/FinEaseHomeAndAdmin/APIs/PublicLeadsService";
import { PublicLead } from "@/utils/types/PublicLeads";
import { Badge } from "@/projects/FinEaseHomeAndAdmin/components/ui/badge";
import { notify } from "@/common/components/common/Notification";

const EXPORT_PAGE_SIZE = 100;
const MAX_EXPORT_PAGES = 500;

function matchesLeadSearch(lead: PublicLead, q: string): boolean {
  if (!q) {
    return true;
  }
  const email = (lead.email ?? "").toLowerCase();
  const label = (lead.label ?? "").toLowerCase();
  return email.includes(q) || label.includes(q);
}

async function fetchAllPublicLeadsForExport(
  search?: string
): Promise<PublicLead[]> {
  const acc: PublicLead[] = [];
  for (let p = 1; p <= MAX_EXPORT_PAGES; p += 1) {
    const res = await PublicLeadsService.getPublicLeads(
      p,
      EXPORT_PAGE_SIZE,
      search
    );
    const batch = res.results ?? [];
    acc.push(...batch);
    if (batch.length < EXPORT_PAGE_SIZE) {
      break;
    }
  }
  const q = (search ?? "").trim().toLowerCase();
  if (!q) {
    return acc;
  }
  return acc.filter(lead => matchesLeadSearch(lead, q));
}

/** Blob download works more reliably than writeFile after async fetch (user-activation). */
function downloadXlsxWorkbook(wb: XLSX.WorkBook, fileName: string) {
  const wbout = XLSX.write(wb, {
    bookType: "xlsx",
    type: "array",
  });
  const blob = new Blob([wbout], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}

const WebsiteLeadsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      300
    );
    return () => window.clearTimeout(id);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const {
    data: leadsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["public-leads", page, pageSize, debouncedSearch],
    queryFn: () =>
      PublicLeadsService.getPublicLeads(
        page,
        pageSize,
        debouncedSearch || undefined
      ),
    enabled: !!page && !!pageSize,
  });

  const total = leadsResponse?.count ?? 0;

  const filteredLeads = useMemo(() => {
    const rows = leadsResponse?.results ?? [];
    const q = debouncedSearch.toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(lead => matchesLeadSearch(lead, q));
  }, [leadsResponse?.results, debouncedSearch]);

  const handleSyncData = async () => {
    try {
      const result = await refetch();
      if (result.isError) {
        notify.error(
          "Sync failed",
          (result.error as Error)?.message ?? "Could not refresh leads."
        );
        return;
      }
      notify.success("Synced", "Website leads updated from server.");
    } catch (e: unknown) {
      notify.error(
        "Sync failed",
        e instanceof Error ? e.message : "Could not refresh leads."
      );
    }
  };

  const handleExportXlsx = async () => {
    setExportLoading(true);
    try {
      let list: PublicLead[];
      let usedVisiblePageOnly = false;

      if (selectedRowKeys.length > 0) {
        // Export only selected rows (already in memory)
        const selectedSet = new Set(selectedRowKeys.map(String));
        list = filteredLeads.filter(l => selectedSet.has(String(l.id)));
        if (list.length === 0) {
          notify.error("No data", "Selected rows not found in current view.");
          return;
        }
      } else {
        // Export all (existing behaviour)
        list = await fetchAllPublicLeadsForExport(debouncedSearch || undefined);
        if (list.length === 0 && filteredLeads.length > 0) {
          list = filteredLeads;
          usedVisiblePageOnly = true;
        }
        if (list.length === 0) {
          notify.error("No data", "No leads to export.");
          return;
        }
      }

      const rows = list.map(lead => ({
        Email: lead.email ?? "",
        Label: lead.label ?? "",
        Status: lead.is_active ? "Active" : "Inactive",
        "Created On": lead.created_on ?? "",
        "Updated On": lead.updated_on ?? "",
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, "Website Leads");
      const datePart = new Date().toISOString().split("T")[0];
      downloadXlsxWorkbook(wb, `website_leads_${datePart}.xlsx`);
      notify.success(
        "Exported",
        selectedRowKeys.length > 0
          ? `${rows.length} selected lead(s) downloaded as Excel.`
          : usedVisiblePageOnly
            ? `${rows.length} lead(s) exported (current table page).`
            : `${rows.length} lead(s) downloaded as Excel.`
      );
    } catch (e: unknown) {
      notify.error(
        "Export failed",
        e instanceof Error ? e.message : "Could not export leads."
      );
    } finally {
      setExportLoading(false);
    }
  };

  const columns: TableColumnsType<PublicLead> = useMemo(
    () => [
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 260,
        render: (email: string) => (
          <span className="font-medium text-secondary">{email ?? "—"}</span>
        ),
      },
      {
        title: "Label",
        dataIndex: "label",
        key: "label",
        width: 180,
        render: (label: string) => (
          <Badge variant="outline" className="font-medium">
            {label ?? "—"}
          </Badge>
        ),
      },
      {
        title: "Status",
        dataIndex: "is_active",
        key: "is_active",
        width: 120,
        render: (is_active: boolean) => (
          <Badge
            variant={is_active ? "default" : "secondary"}
            className={is_active ? "bg-icon-teal text-primary" : ""}
          >
            {is_active ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        title: "Created On",
        dataIndex: "created_on",
        key: "created_on",
        width: 180,
        render: (date: string) =>
          date
            ? new Date(date).toLocaleString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—",
      },
      {
        title: "Updated On",
        dataIndex: "updated_on",
        key: "updated_on",
        width: 180,
        render: (date: string) =>
          date
            ? new Date(date).toLocaleString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })
            : "—",
      },
    ],
    []
  );

  return (
    <div className="space-y-6 p-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <Mail className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Website Leads
              </h1>
              <p className="text-white/70">
                Public leads from website signups (News, Launching Soon, etc.)
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => void handleSyncData()}
              disabled={isFetching}
            >
              <RefreshCw className="h-4 w-4" /> Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={() => void handleExportXlsx()}
              disabled={exportLoading || selectedRowKeys.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRowKeys.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRowKeys.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Card className="rounded-xl border-none shadow-sm">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by email or label..."
              className="h-11 rounded-xl bg-gray-50 pl-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <AntDTable
        data={filteredLeads}
        columns={columns}
        rowKey="id"
        loading={isLoading || isFetching}
        showSettings
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: keys => setSelectedRowKeys(keys),
          preserveSelectedRowKeys: false,
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize ?? 10);
          },
          showTotal: (totalCount, range) =>
            `${range[0]}-${range[1]} of ${totalCount} leads`,
        }}
        emptyText="No leads found"
      />
    </div>
  );
};

export default WebsiteLeadsPage;
